import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const SaveTimerEntryModal = ({ open, onClose, timerData, onSave }) => {
  const [description, setDescription] = useState(timerData?.description || '');
  const [entryType, setEntryType] = useState(timerData?.entry_type || 'work');
  const [isSaving, setIsSaving] = useState(false);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatTime = (date) => {
    return format(new Date(date), 'h:mm a');
  };

  const handleSave = async () => {
    // Validate description
    if (!description || description.trim().length === 0) {
      toast.error('Please add a description of what you worked on');
      return;
    }

    if (description.trim().length < 3) {
      toast.error('Description too short. Please add more details.');
      return;
    }

    setIsSaving(true);

    try {
      const response = await axios.post(`${API}/timer/stop`, {
        description: description.trim(),
        entry_type: entryType
      });

      toast.success(`Saved ${response.data.total_time}!`);
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error('Failed to save entry. Please try again.');
      setIsSaving(false);
    }
  };

  const handleDiscard = async () => {
    const confirmed = window.confirm('Discard this time entry? This cannot be undone.');
    if (!confirmed) return;

    try {
      await axios.post(`${API}/timer/discard`);
      toast.info('Timer discarded');
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Failed to discard timer:', error);
      toast.error('Failed to discard timer');
    }
  };

  if (!timerData || !open) return null;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container - centers modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[95vw] md:max-w-[500px] max-h-[85vh] md:max-h-[80vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700 transition-colors">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-700 dark:to-blue-700 px-4 py-4 flex items-center justify-between flex-shrink-0 transition-colors">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <Dialog.Title className="text-lg sm:text-xl font-semibold text-white transition-colors">
                Save Time Entry
              </Dialog.Title>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors flex-shrink-0 min-w-[44px] min-h-[44px]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white dark:bg-gray-800 transition-colors">
            {/* Duration Summary */}
            <div className="text-center p-4 sm:p-6 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800 transition-colors">
              <div className="text-4xl sm:text-5xl font-bold font-mono text-gray-900 dark:text-gray-100 mb-2 transition-colors">
                {formatDuration(timerData.duration_minutes)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center justify-center gap-2 transition-colors">
                <Clock className="w-4 h-4" />
                {formatTime(timerData.start_time)} - {formatTime(timerData.end_time)}
              </div>
            </div>

            {/* Project (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Project</label>
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 rounded-lg transition-colors">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: timerData.project_color }}
                />
                <span className="font-semibold text-gray-800 dark:text-gray-200 transition-colors">{timerData.project_name}</span>
              </div>
            </div>

            {/* Description (Required!) */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                What did you work on? <span className="text-red-600 dark:text-red-400 transition-colors">*</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you worked on during this time..."
                rows={4}
                className="w-full px-4 py-3 text-base border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 resize-none transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                style={{ fontSize: '16px' }}
                autoFocus
                data-testid="description-input"
              />
              {description.trim().length === 0 && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 transition-colors">
                  Please add a description before saving
                </p>
              )}
              {description.trim().length > 0 && description.trim().length < 3 && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 transition-colors">
                  Description is too short. Add more details.
                </p>
              )}
            </div>

            {/* Entry Type */}
            <div>
              <label htmlFor="entry-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                Entry Type
              </label>
              <select
                id="entry-type"
                value={entryType}
                onChange={(e) => setEntryType(e.target.value)}
                className="w-full px-4 py-3 text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors font-medium"
                style={{ fontSize: '16px' }}
                data-testid="entry-type-select"
              >
                <option value="work">Work</option>
                <option value="meeting">Meeting</option>
                <option value="call">Call</option>
                <option value="break">Break</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Add bottom padding for iOS keyboard */}
            <div className="h-4"></div>
          </div>

        {/* Footer - Fixed at bottom */}
        <div 
          className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-white dark:bg-gray-800 flex flex-col gap-2 flex-shrink-0 rounded-b-3xl sm:rounded-b-2xl transition-colors"
          style={{ 
            position: 'sticky',
            bottom: 0
          }}
        >
          {/* Mobile: Stack all buttons vertically */}
          {/* Desktop: Discard on left, Cancel/Save on right */}
          <div className="hidden sm:flex sm:items-center sm:justify-between">
            <button
              onClick={handleDiscard}
              disabled={isSaving}
              className="flex items-center justify-center gap-2 px-4 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: '48px' }}
              data-testid="discard-button"
            >
              <Trash2 className="w-4 h-4" />
              <span>Discard Entry</span>
            </button>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '48px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !description.trim() || description.trim().length < 3}
                className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ minHeight: '48px' }}
                data-testid="save-button-desktop"
              >
                {isSaving ? 'Saving...' : 'Save Entry'}
              </button>
            </div>
          </div>

          {/* Mobile: All buttons stacked vertically */}
          <div className="flex flex-col gap-2 sm:hidden">
            <button
              onClick={handleSave}
              disabled={isSaving || !description.trim() || description.trim().length < 3}
              className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ minHeight: '48px' }}
              data-testid="save-button-mobile"
            >
              {isSaving ? 'Saving...' : 'Save Entry'}
            </button>
            
            <button
              onClick={onClose}
              disabled={isSaving}
              className="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: '48px' }}
            >
              Cancel
            </button>

            <button
              onClick={handleDiscard}
              disabled={isSaving}
              className="w-full flex items-center justify-center gap-2 py-3 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] border border-red-200 dark:border-red-800"
              data-testid="discard-button-mobile"
            >
              <Trash2 className="w-4 h-4" />
              <span>Discard Entry</span>
            </button>
          </div>
        </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default SaveTimerEntryModal;
